import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import logo from "../assets/images/logo.jpeg";

const navItems = [
  { label: 'Home', path: '/' },
  {
    label: 'About',
    path: '/about',
    children: [
      { label: 'Who We Are', path: '/about', hash: 'overview' },
      { label: 'Our Culture', path: '/about', hash: 'culture' },
      { label: 'Our Values', path: '/about', hash: 'values' },
      { label: 'Capabilities', path: '/about', hash: 'capabilities' },
    ],
  },
  {
    label: 'Services',
    path: '/services',
    children: [
      { label: 'Electronic Design', path: '/services', hash: 'design' },
      { label: 'PCB Fabrication', path: '/services', hash: 'pcb' },
      { label: 'EMS Solutions', path: '/services', hash: 'ems' },
      { label: 'Box Build Assembly', path: '/services', hash: 'boxbuild' },
      { label: 'Component Sourcing', path: '/services', hash: 'sourcing' },
      { label: 'Testing & Validation', path: '/services', hash: 'testing' },
    ],
  },
  { label: 'Industries', path: '/industries' },
  { label: 'Case Studies', path: '/case-studies' },
  { label: 'Technology', path: '/technology' },
  { label: 'Careers', path: '/careers' },
  { label: 'Contact', path: '/contact' },
];

// Offset for fixed navbar height (adjust if your navbar height differs)
const SCROLL_OFFSET = 80;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Handle hash scrolling after navigation
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Wait for page to render, then scroll
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location]);

  const handleDropdownClick = (child, e) => {
    e.preventDefault();
    const targetPath = child.path;
    const targetHash = child.hash;

    if (location.pathname === targetPath) {
      // Already on the page — just scroll
      const el = document.getElementById(targetHash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      // Navigate to page with hash, useEffect above will handle scroll
      navigate(`${targetPath}#${targetHash}`);
    }

    setActiveDropdown(null);
    setMobileOpen(false);
  };

  return (
    <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`} ref={navRef}>
      <div className="navbar__inner container">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <img src={logo} alt="Zxeon FS Logo" className="navbar__logo-img" />
        </Link>

        {/* Desktop nav */}
        <ul className="navbar__links">
          {navItems.map((item) => (
            <li
              key={item.path}
              className="nav-item"
              onMouseEnter={() => item.children && setActiveDropdown(item.path)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <NavLink
                to={item.path}
                className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
              >
                {item.label}
                {item.children && <span className="nav-chevron">▾</span>}
              </NavLink>

              {item.children && activeDropdown === item.path && (
                <div className="nav-dropdown">
                  <div className="nav-dropdown__inner">
                    {item.children.map((child) => (
                      <a
                        key={`${child.path}#${child.hash}`}
                        href={`${child.path}#${child.hash}`}
                        className="nav-dropdown__item"
                        onClick={(e) => handleDropdownClick(child, e)}
                      >
                        <span className="dropdown-dot" />
                        {child.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <div className="navbar__cta">
          <Link to="/contact" className="btn-primary btn-sm">Get a Quote</Link>
        </div>

        {/* Hamburger */}
        <button
          className={`navbar__hamburger${mobileOpen ? ' open' : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`navbar__mobile${mobileOpen ? ' open' : ''}`}>
        <ul className="mobile-links">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </NavLink>
              {item.children && (
                <ul className="mobile-sub">
                  {item.children.map((c) => (
                    <li key={`${c.path}#${c.hash}`}>
                      <a
                        href={`${c.path}#${c.hash}`}
                        className="mobile-sub-link"
                        onClick={(e) => handleDropdownClick(c, e)}
                      >
                        {c.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li className="mobile-cta-wrapper">
            <Link to="/contact" className="btn-primary" onClick={() => setMobileOpen(false)}>
              Get a Quote
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}